import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TalentOnboarding } from './TalentOnboarding';
import { EnhancedAgentOnboarding } from './EnhancedAgentOnboarding';
import { VentureOnboarding } from './VentureOnboarding';
import { BrandOnboarding } from '../brand/BrandOnboarding';
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';
import { supabase } from '@/integrations/supabase/client';

type AccountType = 'talent' | 'agent' | 'brand' | 'venture';

interface OnboardingManagerProps {
  accountType: AccountType;
  onBack: () => void;
  isCompletion?: boolean;
}

export const OnboardingManager: React.FC<OnboardingManagerProps> = ({
  accountType,
  onBack,
  isCompletion = false
}) => {
  const navigate = useNavigate();
  const { setAccountType, refreshUserData } = useUser();

  const { betaAccess } = useUser();

  const handleOnboardingComplete = async (data: any) => {
    console.log('OnboardingManager: Onboarding completed for', accountType);
    
    try {
      // Update local state
      setAccountType(accountType);
      
      // Mark account_imports as claimed via secure RPC
      await supabase.rpc('claim_account_import' as any);
      
      toast.success('Account setup completed successfully!');
      
      // Refresh user data to get the latest setup_completed status from DB
      await refreshUserData();
      
      // If user already has beta access (imported users), go straight to dashboard
      if (betaAccess) {
        console.log('OnboardingManager: User has beta access, redirecting to dashboard');
        navigate('/', { replace: true });
      } else {
        // New self-signup users go to pending-approval
        console.log('OnboardingManager: Redirecting to pending approval');
        navigate('/pending-approval', { replace: true });
      }
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error('Failed to complete setup. Please try again.');
    }
  };

  const handleBrandComplete = async () => {
    await handleOnboardingComplete({});
  };

  // Render the appropriate onboarding component based on account type
  switch (accountType) {
    case 'talent':
      return (
        <TalentOnboarding
          onComplete={handleOnboardingComplete}
          onBack={onBack}
          isCompletion={isCompletion}
        />
      );
    
    case 'agent':
      return (
        <EnhancedAgentOnboarding
          onComplete={handleOnboardingComplete}
          onBack={onBack}
          isCompletion={isCompletion}
        />
      );
    
    case 'venture':
      return (
        <VentureOnboarding
          onComplete={handleOnboardingComplete}
          onBack={onBack}
          isCompletion={isCompletion}
        />
      );
    
    case 'brand':
      return (
        <BrandOnboarding
          onComplete={handleOnboardingComplete}
          onBack={onBack}
          isCompletion={isCompletion}
        />
      );
    
    default:
      return null;
  }
};